import React, { useRef, useState } from "react";
import contactinfoimg1 from '../assets/images/contact-info-img-1.jpg';
import contactinfoimg2 from '../assets/images/contact-info-img-2.jpg';
import iconmail from '../assets/images/icon-mail.svg';
import contactusimg from '../assets/images/contact-us-img.jpg';
import iconphone from '../assets/images/icon-phone.svg';
import iconlocation from '../assets/images/icon-location.svg';
import ReCAPTCHA from "react-google-recaptcha";

function Contact() {
  const recaptchaRef = useRef();
  const [formData, setFormData] = useState({
    fname: "",
    lname: "",
    phone: "",
    email: "",
    message: "",
    consent: false
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  // Validation functions
  const validateField = (name, value) => {
    switch (name) {
      case 'fname':
        if (!value.trim()) return "First name is required";
        if (!/^[A-Za-z]+$/.test(value))
          return "First name can contain only letters";
        return "";

      case 'lname':
        if (!value.trim()) return "Last name is required";
        if (!/^[A-Za-z]+$/.test(value))
          return "Last name can contain only letters";
        return "";
      case 'phone':
        if (!value) return "Phone number is required";
        if (value.length !== 10) return "Phone number must be exactly 10 digits";
        return "";
      case 'email':
        if (!value) return "Email is required";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return "Please enter a valid email address";
        return "";
      case 'message':
        return !value.trim() ? "Message is required" : "";
      case 'consent':
        return !value ? "Please agree to receive messages" : "";
      default:
        return "";
    }
  };

  const validateForm = () => {
    const newErrors = {};
    newErrors.fname = validateField('fname', formData.fname);
    newErrors.lname = validateField('lname', formData.lname);
    newErrors.phone = validateField('phone', formData.phone);
    newErrors.email = validateField('email', formData.email);
    newErrors.message = validateField('message', formData.message);
    newErrors.consent = validateField('consent', formData.consent);

    setErrors(newErrors);
    return Object.values(newErrors).every(error => error === "");
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    if (name === "fname" || name === "lname") {
      // Sirf letters aur space allow
      const filteredValue = value.replace(/[^A-Za-z\s]/g, "");

      setFormData((prev) => ({
        ...prev,
        [name]: filteredValue,
      }));
      return;
    }

    if (name === "phone") {
      // Only allow digits and limit to 10 characters
      const digitsOnly = value.replace(/\D/g, "");
      if (digitsOnly.length <= 10) {
        setFormData({
          ...formData,
          [name]: digitsOnly
        });

        // Clear error when user starts typing
        if (errors[name]) {
          setErrors({
            ...errors,
            [name]: validateField(name, digitsOnly)
          });
        }
      }
    } else {
      setFormData({
        ...formData,
        [name]: fieldValue
      });

      // Clear error when user starts typing
      if (errors[name]) {
        setErrors({
          ...errors,
          [name]: validateField(name, fieldValue)
        });
      }
    }

    // Mark field as touched
    if (!touched[name]) {
      setTouched({
        ...touched,
        [name]: true
      });
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({
      ...touched,
      [name]: true
    });

    // Validate on blur
    const error = validateField(name, formData[name]);
    setErrors({
      ...errors,
      [name]: error
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);

    // Mark all fields as touched
    const allTouched = {};
    Object.keys(formData).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    // Validate all fields
    const isValid = validateForm();

    if (!isValid) {
      // Scroll to first error
      const firstError = document.querySelector('.error-border');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const token = recaptchaRef.current?.getValue();

    if (!token) {
      alert("Please verify captcha");
      return;
    }

    if (!formData.consent) {
      alert("Please agree to receive messages");
      return;
    }

    alert("Form Submitted Successfully!");
    console.log("Captcha Token:", token);
    console.log("Form Data:", formData);

    // Reset form
    setFormData({
      fname: "",
      lname: "",
      phone: "",
      email: "",
      message: "",
      consent: false
    });
    setErrors({});
    setTouched({});
    setSubmitted(false);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }

    window.location.href = "/";
  };

  // Determine input class based on validation state
  const getInputClass = (fieldName) => {
    if (submitted || touched[fieldName]) {
      if (errors[fieldName]) {
        return "error-border";
      } else if (formData[fieldName] && !errors[fieldName]) {
        return "success-border";
      }
    }
    return "";
  };

  return (
    <>
      <div className="page-header">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-12">
              <div className="page-header-box">
                <h1 className="text-anime-style-2" data-cursor="-opaque">
                  Contact us
                </h1>
                <nav className="wow fadeInUp"></nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-contact-us">
        <div className="container">
          <div className="row">
            <div className="col-lg-4 col-md-6">
              <div className="contact-info-item wow fadeInUp">
                <div className="contact-info-img">
                  <figure className="image-anime">
                    <img src={contactinfoimg1} alt="contactinfoimg1" />
                  </figure>
                </div>
                <div className="contact-info-body">
                  <div className="icon-box">
                    <img src={iconphone} alt="iconphone" />
                  </div>
                  <div className="contact-info-content"></div>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="contact-info-item wow fadeInUp" data-wow-delay="0.2s">
                <div className="contact-info-img">
                  <figure className="image-anime">
                    <img src={contactinfoimg2} alt="contactinfoimg2" />
                  </figure>
                </div>
                <div className="contact-info-body">
                  <div className="icon-box">
                    <img src={iconmail} alt="iconmail" />
                  </div>
                  <div className="contact-info-content">
                    <h3>send us e-mail</h3>
                    <p>sales@payinfintech.com</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="contact-info-item wow fadeInUp" data-wow-delay="0.4s">
                <div className="contact-info-img">
                  <figure className="image-anime">
                    <img src={contactinfoimg1} alt="contactinfoimg1" />
                  </figure>
                </div>
                <div className="contact-info-body">
                  <div className="icon-box">
                    <img src={iconlocation} alt="iconlocation" />
                  </div>
                  <div className="contact-info-content">
                    <h3>finance office address</h3>
                    <p>
                      Flat No. F-1, Plot No. 238-A, Nemi Nagar Vistar, Vaishali Nagar, Near 200 Feet Bypass, Jaipur – 302021, Rajasthan
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="contact-form-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="contact-form-img">
                <figure className="image-anime reveal">
                  <img src={contactusimg} alt="contactusimg" />
                </figure>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="section-title">
                <h3 className="wow fadeInUp">contact us</h3>
                <h2 className="text-anime-style-2" data-cursor="-opaque">
                  Get in Touch <span>with Us</span>
                </h2>
                <p className="wow fadeInUp" data-wow-delay="0.2s">
                  Have questions or need assistance? Reach out to us today! We're
                  here to provide expert solutions and friendly support.
                </p>
              </div>
              <div className="contact-form">
                <form
                  id="contactForm"
                  onSubmit={handleSubmit}
                  data-toggle="validator"
                  className="wow fadeInUp"
                  data-wow-delay="0.4s"
                >
                  <div className="row">
                    <div className="form-group col-md-6 mb-4">
                      <input
                        type="text"
                        name="fname"
                        className={`form-control ${getInputClass('fname')}`}
                        id="fname"
                        placeholder="First name"

                        value={formData.fname}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                      />
                      {(submitted || touched.fname) && errors.fname && (
                        <div className="error-message">{errors.fname}</div>
                      )}
                      <div className="help-block with-errors" />
                    </div>

                    <div className="form-group col-md-6 mb-4">
                      <input
                        type="text"
                        name="lname"
                        className={`form-control ${getInputClass('lname')}`}
                        id="lname"
                        placeholder="Last name"

                        value={formData.lname}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                      />
                      {(submitted || touched.lname) && errors.lname && (
                        <div className="error-message">{errors.lname}</div>
                      )}
                      <div className="help-block with-errors" />
                    </div>

                    <div className="form-group col-md-6 mb-4">
                      <input
                        type="tel"
                        name="phone"
                        className={`form-control ${getInputClass('phone')}`}
                        id="phone"
                        placeholder="Phone no (10 digits)"

                        maxLength={10}
                        value={formData.phone}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        title="Please enter exactly 10 digits"
                      />
                      <small className="text-muted">Enter exactly 10 digits (numbers only)</small>
                      {(submitted || touched.phone) && errors.phone && (
                        <div className="error-message">{errors.phone}</div>
                      )}
                      <div className="help-block with-errors" />
                    </div>

                    <div className="form-group col-md-6 mb-4">
                      <input
                        type="email"
                        name="email"
                        className={`form-control ${getInputClass('email')}`}
                        id="email"
                        placeholder="E-mail address"

                        value={formData.email}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                      />
                      {(submitted || touched.email) && errors.email && (
                        <div className="error-message">{errors.email}</div>
                      )}
                      <div className="help-block with-errors" />
                    </div>

                    <div className="form-group col-md-12 mb-5">
                      <textarea
                        name="message"
                        className={`form-control ${getInputClass('message')}`}
                        id="message"
                        rows={4}
                        placeholder="Message"
                        value={formData.message}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                      />
                      {(submitted || touched.message) && errors.message && (
                        <div className="error-message">{errors.message}</div>
                      )}
                      <div className="help-block with-errors" />
                    </div>

                    <div className="col-md-12 mb-4">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey="6LeQNmosAAAAAI8uHovKYfpGcLbHvHTVxnVwMW00"
                      />
                    </div>

                    <div className="col-md-12 mb-4 termsss">
                      <div className="d-flex gap-2 align-items-start">
                        <input
                          type="checkbox"
                          id="consent"
                          name="consent"
                          style={{ marginTop: "12px" }}
                          checked={formData.consent}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                        />
                        <label htmlFor="consent">
                          I agree to receive messages from Payinfintech Pvt Ltd and its representatives
                          through WhatsApp RSC, Email, and other Communication Channels
                        </label>
                      </div>
                      {(submitted || touched.consent) && errors.consent && (
                        <div className="error-message" style={{ marginTop: "5px" }}>{errors.consent}</div>
                      )}
                    </div>

                    <div className="col-md-12">
                      <button type="submit" className="btn-default">
                        send message
                      </button>
                      <div id="msgSubmit" className="h3 hidden" />
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="google-map">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="google-map-iframe">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d96737.10562045308!2d-74.08535042841811!3d40.739265258395164!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c24fa5d33f083b%3A0xc80b8f06e177fe62!2sNew%20York%2C%20NY%2C%20USA!5e0!3m2!1sen!2sin!4v1703158537552!5m2!1sen!2sin"
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Google Map"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .form-control.error-border {
          border: 2px solid #dc3545 !important;
          transition: all 0.3s ease;
        }
        
        .form-control.success-border {
          border: 2px solid #28a745 !important;
          transition: all 0.3s ease;
        }
        
        .error-message {
          color: #dc3545;
          font-size: 12px;
          margin-top: 5px;
          animation: fadeIn 0.3s ease;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .form-control:focus {
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
        }
        
        .form-control.success-border:focus {
          box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
        }
      `}</style>
    </>
  );
}

export default Contact;